<template>
  <NativeDrawer
    v-model="drawer"
    :before-close="handleDrawerClose"
    size="80%"
  >
    <template #header>
      <h4>科室部门信息</h4>
    </template>
    <template #default>
      <div class="department-content">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <NativeSkeleton :rows="5" animated />
        </div>

        <!-- 科室列表 -->
        <div v-else class="department-list">
          <!-- 搜索筛选组件 -->
          <BaseSearch
            :search-form="searchForm"
            :fields="searchFields"
            @search="handleSearch"
            @reset="handleReset"
          />

          <!-- 表格组件 -->
          <DepartmentTable
            :department-list="departmentList"
            @edit="handleEdit"
            @delete="handleDeleteConfirm"
          />

          <!-- 分页组件 -->
          <BasePagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>

        <!-- 编辑表单组件 -->
        <DepartmentForm
          v-model:visible="editDialogVisible"
          :is-edit="isEdit"
          :form-data="formData"
          :submit-loading="submitLoading"
          @submit="handleSubmitForm"
          @cancel="handleFormCancel"
        />
      </div>
    </template>
    <template #footer>
      <div
        style="flex: auto; display: flex; justify-content: flex-end; gap: 10px"
      >
        <NativeButton @click="addClick" type="success">新增科室</NativeButton>
        <NativeButton type="primary" @click="confirmClick">关闭</NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import {
  useDepartmentsStore,
  type DepartmentType,
} from "@/stores/departments";
import { notify } from "@/utils/nativeFeedback";

import type { SearchField } from "@/components/NativeUI/types";

// 导入子组件
import BaseSearch from "./BaseSearch/index.vue";
import DepartmentTable from "./department/DepartmentTable.vue";
import BasePagination from "./BasePagination/index.vue";
import DepartmentForm from "./department/DepartmentForm.vue";

// ==================== Props & Emits ====================
const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
}>();
const departmentsStore = useDepartmentsStore();

// ==================== Drawer 状态 ====================
const drawer = ref(props.visible);

watch(
  () => props.visible,
  (newVal) => {
    drawer.value = newVal;
    if (newVal) {
      loadDepartmentList();
    }
  },
);

watch(drawer, (newVal) => {
  emit("update:visible", newVal);
});

// ==================== 数据相关 ====================
const loading = ref(false);
const allDepartmentList = ref<DepartmentType[]>([]);
const departmentList = ref<DepartmentType[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 搜索表单
const searchForm = ref({
  name: "",
});

// 搜索字段配置
const searchFields: SearchField[] = [
  {
    label: "科室名称",
    prop: "name",
    type: "input",
    placeholder: "请输入科室名称",
    width: "200px",
  },
];

// 筛选后的数据
const filteredList = computed(() => {
  let list = allDepartmentList.value;

  if (searchForm.value.name) {
    list = list.filter((item) => item.name?.includes(searchForm.value.name));
  }

  return list;
});

// 编辑表单
const editDialogVisible = ref(false);
const isEdit = ref(false);
const submitLoading = ref(false);
const formData = ref<Partial<DepartmentType>>({
  achievements: [],
  category: "",
  contact: "",
  descimp: "",
  descmain: "",
  description: "",
  director: "",
  features: [],
  group: "",
  id: 0,
  image: "",
  introduce: "",
  level: "",
  name: "",
  outpatients: "",
  projects: [],
});

// ==================== API 调用 ====================
const updatePageData = () => {
  const list = filteredList.value;
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  departmentList.value = list.slice(start, end);
  total.value = list.length;
};

const loadDepartmentList = async () => {
  loading.value = true;
  try {
    await departmentsStore.refreshDepartments();
    allDepartmentList.value = departmentsStore.getDepartmentsList;
    updatePageData();
  } catch (error) {
    console.error("加载科室列表失败:", error);
    notify("加载科室列表失败", "error");
  } finally {
    loading.value = false;
  }
};

// ==================== 事件处理 ====================
const handleSearch = () => {
  currentPage.value = 1;
  updatePageData();
};

const handleReset = () => {
  searchForm.value = {
    name: "",
  };
  currentPage.value = 1;
  updatePageData();
};

const handleSizeChange = (val: number) => {
  pageSize.value = val;
  currentPage.value = 1;
  updatePageData();
};

const handleCurrentChange = (val: number) => {
  currentPage.value = val;
  updatePageData();
};

const addClick = () => {
  isEdit.value = false;
  formData.value = {
    achievements: [],
    category: "",
    contact: "",
    descimp: "",
    descmain: "",
    description: "",
    director: "",
    features: [],
    group: "",
    id: 0,
    image: "",
    introduce: "",
    level: "",
    name: "",
    outpatients: "",
    projects: [],
  };
  editDialogVisible.value = true;
};

const handleEdit = (row: DepartmentType) => {
  isEdit.value = true;
  formData.value = {
    achievements: row.achievements || [],
    category: row.category || "",
    contact: row.contact || "",
    descimp: row.descimp || "",
    descmain: row.descmain || "",
    description: row.description || "",
    director: row.director || "",
    features: row.features || [],
    group: row.group || "",
    id: row.id || 0,
    image: row.image || "",
    introduce: row.introduce || "",
    level: row.level || "",
    name: row.name || "",
    outpatients: row.outpatients || "",
    projects: row.projects || [],
  };
  editDialogVisible.value = true;
};

const handleDeleteConfirm = async (row: DepartmentType) => {
  if (!window.confirm(`确认删除科室 "${row.name}" 吗?`)) return;

  if (await departmentsStore.deleteDepartment(row.id)) {
    allDepartmentList.value = departmentsStore.getDepartmentsList;
    updatePageData();
  }
};

const handleSubmitForm = async (formData: Partial<DepartmentType>) => {
  submitLoading.value = true;
  try {
    const success =
      isEdit.value && formData.id
        ? await departmentsStore.updateDepartmentById(formData.id, formData)
        : await departmentsStore.createDepartment(formData);

    if (success) {
      allDepartmentList.value = departmentsStore.getDepartmentsList;
      updatePageData();
      editDialogVisible.value = false;
    }
  } finally {
    submitLoading.value = false;
  }
};

const handleFormCancel = () => {
  formData.value = {
    achievements: [],
    category: "",
    contact: "",
    descimp: "",
    descmain: "",
    description: "",
    director: "",
    features: [],
    group: "",
    id: 0,
    image: "",
    introduce: "",
    level: "",
    name: "",
    outpatients: "",
    projects: [],
  };
  isEdit.value = false;
};

const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

const confirmClick = () => {
  drawer.value = false;
};

// 监听筛选结果变化
watch(filteredList, () => {
  currentPage.value = 1;
  updatePageData();
});
</script>

<style scoped lang="scss">
.department-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;

  .loading-container {
    padding: 20px;
  }

  .department-list {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    // 搜索筛选区域 - 固定在顶部
    :deep(.search-filter) {
      flex-shrink: 0;
    }

    // 表格容器 - 占据剩余空间并可滚动
    :deep(.department-table) {
      flex: 1;
      min-height: 0;
      overflow: auto;
    }

    :deep(.native-table) {
      flex: 1;
      min-height: 0;
    }

    // 分页组件 - 固定在底部
    :deep(.pagination-container) {
      flex-shrink: 0;
      border-top: 1px solid #ebeef5;
      background-color: #fff;
    }
  }
}
</style>
